import React, { useEffect } from 'react';
import { Product } from '../../types/product';
import { format } from 'date-fns';

interface PriceListPrintPreviewProps {
  products: Product[];
  productInventorySummary: Record<string, { total: number; available: number }>;
  title: string;
  includeStock: boolean;
  includeSpecs: boolean;
  includeImages: boolean;
  groupByCategory: boolean;
  onClose: () => void;
}

const PriceListPrintPreview: React.FC<PriceListPrintPreviewProps> = ({
  products,
  productInventorySummary,
  title,
  includeStock,
  includeSpecs,
  includeImages,
  groupByCategory,
  onClose
}) => {
  // Kategória szerinti csoportosítás
  const groupedProducts: Record<string, Product[]> = {};
  if (groupByCategory) {
    products.forEach(product => {
      const category = product.category?.level1 || 'Kategória nélkül';
      if (!groupedProducts[category]) {
        groupedProducts[category] = [];
      }
      groupedProducts[category].push(product);
    });
  } else {
    groupedProducts['Összes termék'] = products;
  }

  useEffect(() => {
    // ESC billentyűre bezárás
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Nyomtatás stílusok */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-preview, #print-preview * {
            visibility: visible;
          }
          #print-preview {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .page-break {
            page-break-before: always;
          }
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
        }

        @media screen {
          #print-preview {
            background: white;
            max-width: 210mm;
            margin: 0 auto;
            padding: 20mm;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>

      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center no-print">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Nyomtatási előnézet
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrint}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                🖨️ Nyomtatás / PDF mentés
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Bezárás
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-100 dark:bg-gray-900">
            <div id="print-preview">
              {/* Fejléc */}
              <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1F2937', marginBottom: '10px' }}>
                  {title}
                </h1>
                <p style={{ fontSize: '12px', color: '#6B7280' }}>
                  Generálva: {format(new Date(), 'yyyy. MM. dd. HH:mm')}
                </p>
              </div>

              {/* Kategóriák */}
              {Object.keys(groupedProducts).sort().map((category, catIndex) => {
                const categoryProducts = groupedProducts[category];
                
                return (
                  <div key={category} className={catIndex > 0 ? 'page-break' : ''}>
                    {/* Kategória címsor */}
                    {groupByCategory && Object.keys(groupedProducts).length > 1 && (
                      <h2 style={{ 
                        fontSize: '18px', 
                        fontWeight: 'bold', 
                        color: '#4F46E5', 
                        marginBottom: '20px',
                        borderBottom: '2px solid #4F46E5',
                        paddingBottom: '8px'
                      }}>
                        {category}
                      </h2>
                    )}

                    {/* Katalógus stílus (képekkel) */}
                    {includeImages ? (
                      <div style={{ display: 'grid', gap: '20px' }}>
                        {categoryProducts.map((product) => {
                          const productAny = product as any;
                          const specs: string[] = [];
                          
                          if (includeSpecs && productAny.specifications) {
                            if (productAny.specifications.cpu) specs.push(`CPU: ${productAny.specifications.cpu}`);
                            if (productAny.specifications.ram) specs.push(`RAM: ${productAny.specifications.ram}`);
                            if (productAny.specifications.storage) specs.push(`Tárhely: ${productAny.specifications.storage}`);
                            if (productAny.specifications.screen_size) specs.push(`Kijelző: ${productAny.specifications.screen_size}`);
                            if (productAny.specifications.graphics) specs.push(`GPU: ${productAny.specifications.graphics}`);
                            if (productAny.specifications.os) specs.push(`OS: ${productAny.specifications.os}`);
                          }

                          const inventory = productInventorySummary[product.id];

                          return (
                            <div 
                              key={product.id}
                              style={{
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '15px',
                                display: 'grid',
                                gridTemplateColumns: includeImages ? '150px 1fr' : '1fr',
                                gap: '15px',
                                backgroundColor: '#FFFFFF'
                              }}
                            >
                              {/* Kép */}
                              {product.thumbnailUrl && (
                                <div style={{ textAlign: 'center' }}>
                                  <img 
                                    src={product.thumbnailUrl} 
                                    alt={product.name}
                                    style={{
                                      width: '140px',
                                      height: '140px',
                                      objectFit: 'contain',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '4px',
                                      padding: '5px'
                                    }}
                                  />
                                </div>
                              )}

                              {/* Termék adatok */}
                              <div>
                                <h3 style={{ 
                                  fontSize: '16px', 
                                  fontWeight: 'bold', 
                                  color: '#1F2937',
                                  marginBottom: '10px'
                                }}>
                                  {product.name}
                                </h3>
                                
                                <div style={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: '10px',
                                  marginBottom: '10px'
                                }}>
                                  <div>
                                    <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '2px' }}>
                                      Nettó ár
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#059669' }}>
                                      {product.netPrice ? `${product.netPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-'}
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '2px' }}>
                                      Bruttó ár
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#DC2626' }}>
                                      {product.grossPrice ? `${product.grossPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-'}
                                    </div>
                                  </div>
                                </div>

                                {includeStock && (
                                  <div style={{ fontSize: '12px', color: '#4B5563', marginBottom: '8px' }}>
                                    📦 Készlet: <strong>{inventory ? `${inventory.available} db` : '0 db'}</strong>
                                  </div>
                                )}

                                {specs.length > 0 && (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    color: '#6B7280',
                                    fontStyle: 'italic',
                                    borderTop: '1px solid #E5E7EB',
                                    paddingTop: '8px'
                                  }}>
                                    {specs.join(' • ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Táblázat stílus */
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '11px'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#4F46E5', color: 'white' }}>
                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #E5E7EB' }}>
                              Terméknév
                            </th>
                            <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #E5E7EB', width: '100px' }}>
                              Nettó ár
                            </th>
                            <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #E5E7EB', width: '100px' }}>
                              Bruttó ár
                            </th>
                            {includeStock && (
                              <th style={{ padding: '10px', textAlign: 'center', border: '1px solid #E5E7EB', width: '80px' }}>
                                Készlet
                              </th>
                            )}
                            {includeSpecs && (
                              <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #E5E7EB' }}>
                                Specifikáció
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {categoryProducts.map((product, index) => {
                            const productAny = product as any;
                            const specs: string[] = [];
                            
                            if (includeSpecs && productAny.specifications) {
                              if (productAny.specifications.cpu) specs.push(productAny.specifications.cpu);
                              if (productAny.specifications.ram) specs.push(productAny.specifications.ram);
                              if (productAny.specifications.storage) specs.push(productAny.specifications.storage);
                              if (productAny.specifications.screen_size) specs.push(productAny.specifications.screen_size);
                              if (productAny.specifications.graphics) specs.push(productAny.specifications.graphics);
                              if (productAny.specifications.os) specs.push(productAny.specifications.os);
                            }

                            const inventory = productInventorySummary[product.id];

                            return (
                              <tr 
                                key={product.id}
                                style={{ backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'white' }}
                              >
                                <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>
                                  {product.name}
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #E5E7EB', textAlign: 'right', color: '#059669', fontWeight: 'bold' }}>
                                  {product.netPrice ? `${product.netPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-'}
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #E5E7EB', textAlign: 'right', color: '#DC2626', fontWeight: 'bold' }}>
                                  {product.grossPrice ? `${product.grossPrice.toLocaleString('hu-HU')} ${product.currency || 'HUF'}` : '-'}
                                </td>
                                {includeStock && (
                                  <td style={{ padding: '8px', border: '1px solid #E5E7EB', textAlign: 'center' }}>
                                    {inventory ? `${inventory.available} db` : '0 db'}
                                  </td>
                                )}
                                {includeSpecs && (
                                  <td style={{ padding: '8px', border: '1px solid #E5E7EB', fontSize: '10px' }}>
                                    {specs.join(', ') || '-'}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}

              {/* Lábléc */}
              <div style={{ 
                marginTop: '40px', 
                paddingTop: '20px',
                borderTop: '1px solid #E5E7EB',
                fontSize: '10px',
                color: '#6B7280',
                textAlign: 'center'
              }}>
                <p>YouCRM - {format(new Date(), 'yyyy. MM. dd.')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PriceListPrintPreview;
